// Global
import React, { useMemo } from 'react';

// Local
import { Navigation } from '@generated/Navigation/Header.model';
import { Header } from './Header';
import ImageWrapper from 'components/helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import { PureHeaderPrimaryLinkBar } from './types';
import { transformToPureHeaderPrimaryLinkBar } from './Header.utils';
import { SvgImageWrapper } from 'components/helpers/SitecoreWrappers/SvgImageWrapper/SvgImageWrapper';

type LanguageLink = { text?: string; locale: string };
import { SearchBarVariantsType } from 'lib/services/models/types';
import { ComponentParams } from '@sitecore-content-sdk/nextjs';
import { useSiteSettings } from 'lib/hooks/useSiteSettings';

type HeaderProps = Navigation.Header.Header_Component &
  Omit<Navigation.Header.RenderingParameters.HeaderParameters_Component, 'params'> & {
    params: ComponentParams & {
      searchBarVariants: SearchBarVariantsType;
    };
  };

export const Default = (props: HeaderProps) => {
  const siteSettings = useSiteSettings();
  const hideFavouritesButton = (
    props.fields as { hideFavouritesButton?: { value?: boolean | string } } | undefined
  )?.hideFavouritesButton?.value;

  const links: PureHeaderPrimaryLinkBar[] = transformToPureHeaderPrimaryLinkBar(
    props.fields?.primaryLinks || []
  );

  // Simplified mapping for supported languages — reducer keeps the array clean (no `undefined` members)
  const languageLinks: LanguageLink[] = (
    (props.fields?.supportedLanguages as Navigation.Header.Language[] | undefined) ?? []
  ).reduce<LanguageLink[]>((acc, lang) => {
    const text = lang.fields?.linkText?.value?.trim();
    const locale = lang.fields?.locale?.name?.trim() ?? lang.fields?.locale?.displayName?.trim();

    if (locale) acc.push({ text, locale });
    return acc;
  }, []);

  // Use MyFavouritesIcon from site settings for favourites icon
  // Falls back to heart icon in MyFavourites component if not configured
  const favouritesIcon = useMemo(() => {
    const field = siteSettings?.MyFavouritesIcon;
    return field?.value?.src ? <ImageWrapper field={field} /> : undefined;
  }, [siteSettings]);

  return (
    <Header
      logo={
        props.fields?.logo?.value?.src?.includes('.svg') ? (
          <SvgImageWrapper
            src={props.fields?.logo?.value?.src || ''}
            alt={(props.fields?.logo?.value?.alt as string) || ''}
            shouldHaveAriaLabel={false}
          />
        ) : (
          <ImageWrapper field={props.fields?.logo} />
        )
      }
      primaryLinkBar={links}
      hideUserAccountCTA={props.fields?.hideUserAccountCTA?.value}
      hideSearchToggle={props.fields?.hideSearchBar?.value}
      hideFavouritesButton={hideFavouritesButton === true || String(hideFavouritesButton) === '1'}
      searchBarVariant={props.params?.searchBarVariants as SearchBarVariantsType}
      // NEW props for language bar
      hideLanguageToggle={props.fields?.hideLanguageToggle?.value}
      languageLinks={languageLinks}
      favouritesIcon={favouritesIcon}
    />
  );
};
